// The global domain blocklist: domains we must never send any outbound request
// to (a site asked us to stop, or a legal/abuse reason). An entry covers the
// registrable domain and every subdomain. The safe client refuses blocked
// domains on every request; the crawl and webfetch also skip them entirely.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { api, type BlockedDomainInput } from '../api'
import { ErrorBox, Pill } from '../components'
import { dt } from '../format'

const sources: BlockedDomainInput['source'][] = ['request', 'legal', 'abuse', 'manual']

const empty: BlockedDomainInput = { domain: '', reason: '', requested_by: '', source: 'request' }

export function Blocklist() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['blocklist'], queryFn: api.blocklist })
  const [form, setForm] = useState<BlockedDomainInput>(empty)

  const add = useMutation({
    mutationFn: (b: BlockedDomainInput) => api.addBlocked(b),
    onSuccess: () => {
      setForm(empty)
      qc.invalidateQueries({ queryKey: ['blocklist'] })
    },
  })
  const remove = useMutation({
    mutationFn: (domain: string) => api.removeBlocked(domain),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['blocklist'] }),
  })

  return (
    <section className="page">
      <header className="page-h">
        <div>
          <h1>Blocklist</h1>
          <p className="sub">
            Domains we must never query. An entry covers the domain and all its subdomains, and stops every crawler and outbound
            request. Changes are audited and take effect immediately; the crawl and webfetch Jobs pick them up on their next run.
          </p>
        </div>
      </header>

      <form
        className="card form-grid"
        onSubmit={(e) => {
          e.preventDefault()
          if (form.domain.trim()) add.mutate(form)
        }}
      >
        <label>
          Domain
          <input
            className="mono"
            placeholder="example.se"
            value={form.domain}
            onChange={(e) => setForm({ ...form, domain: e.target.value })}
            aria-label="Domain to block"
          />
        </label>
        <label>
          Source
          <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value as BlockedDomainInput['source'] })}>
            {sources.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <label>
          Requested by
          <input
            placeholder="who asked (e.g. the site's contact)"
            value={form.requested_by}
            onChange={(e) => setForm({ ...form, requested_by: e.target.value })}
            aria-label="Requested by"
          />
        </label>
        <label className="wide">
          Reason
          <input
            placeholder="why it is blocked"
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            aria-label="Reason"
          />
        </label>
        <div>
          <button type="submit" disabled={add.isPending || !form.domain.trim()}>Block domain</button>
        </div>
      </form>
      {add.error && <ErrorBox error={add.error} />}
      {remove.error && <ErrorBox error={remove.error} />}
      {q.error && <ErrorBox error={q.error} />}

      <table className="tbl">
        <thead>
          <tr>
            <th>Domain</th>
            <th>Source</th>
            <th>Reason</th>
            <th>Requested by</th>
            <th>Added</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {(q.data ?? []).map((b) => (
            <tr key={b.domain}>
              <td className="mono">{b.domain}</td>
              <td><Pill kind="neutral">{b.source}</Pill></td>
              <td>{b.reason}</td>
              <td className="muted">{b.requested_by}</td>
              <td className="muted">{dt(b.created_at)}</td>
              <td>
                <button
                  className="btn btn-sm"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(b.domain)}
                >
                  Unblock
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!q.isLoading && q.data?.length === 0 && <p className="muted">No domains are blocked.</p>}
    </section>
  )
}
